import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import "./css/PersonalFinance.css";
import "./css/InvestparaBr.css";
import { exportCourseDocx, openIosStagingTab } from "./utils/docxExport";
import { LanguageContext } from "./LanguageContext";
import { stockTradingCourse as STOCK } from "./CourseData";

const apiUrl = "https://qedeefqyh2.execute-api.us-west-2.amazonaws.com/prod";

// ===== DEV BYPASS =====
const isDev = process.env.NODE_ENV === "development";
const authEnabled = false; // disable auth checks on localhost/dev

const StockTradingBasics = () => {
  const history = useHistory();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { language, translations } = useContext(LanguageContext);
  const [downloading, setDownloading] = useState(false);
  const [showIntro, setShowIntro] = useState(false);

  const MODULE = STOCK.module;
  const LESSONS = STOCK.lessons;

  const [showModule, setShowModule] = useState({ [MODULE]: true });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLessonClick = async (lessonId) => {
    const path = `/lessons/module${MODULE}/lesson${lessonId}?lang=${language}&course=stock-trading-basics`;
    if (authEnabled && !isAuthenticated) {
      localStorage.setItem("auth_redirect_path", path);
      await loginWithRedirect();
      return;
    }
    history.push(path);
  };

  const buildStockRefs = () =>
    LESSONS.map(({ id }) => ({
      moduleKey: `module${MODULE}`,
      lessonKey: `lesson${id}`,
      id,
    })).sort((a, b) => a.id - b.id);

  const handleDownload = async () => {
    const preWin = openIosStagingTab();
    try {
      if (authEnabled && !isAuthenticated) {
        localStorage.setItem("auth_redirect_path", window.location.pathname);
        await loginWithRedirect();
        if (preWin) preWin.close();
        return;
      }

      setDownloading(true);
      const refs = buildStockRefs();
      const docTitle = `${translations.stockTradingBasicsTitle} - ${language.toUpperCase()}`;
      const fileBase = language === "pt" ? "stock-trading-basics-PT" : "stock-trading-basics-EN";

      await exportCourseDocx({
        refs,
        apiUrl,
        language,
        docTitle,
        fileBase,
        labels: {
          courseIntro: translations.courseContent || "",
          moduleLabel: translations.module || "Module",
          showModuleHeadings: false,
          renumberSingleModuleTo1: true,
        },
        preOpenedWin: preWin,
      });
    } catch (e) {
      console.error(e);
      alert("Sorry — couldn't create the Word file.");
      if (preWin) preWin.close();
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="investpara-page">
      <div className="courses-page1">
        <h1 className="investpara-title">
          {language === "pt" ? (
            <>Noções Básicas de <span>Negociação de Ações</span></>
          ) : (
            <>Stock Trading <span>Basics</span></>
          )}
        </h1>

        {/* ===== INTRO CARD ===== */}
        <section className="investpara-introCard">
          <span className="course-badge course-badge--free" style={{ marginBottom: 12 }}>
            {language === "pt" ? "Curso 100% Grátis" : "100% Free Course"}
          </span>
          <h2 className="investpara-subtitle">
            {language === "pt"
              ? "Aprenda os Fundamentos do Mercado de Ações"
              : "Learn the Fundamentals of the Stock Market"}
          </h2>

          <p className="investpara-lead">
            {language === "pt"
              ? "Um curso completo para quem quer entender como funciona o mercado de ações, desde os conceitos básicos até estratégias de investimento."
              : "A complete course for anyone who wants to understand how the stock market works, from basic concepts to investment strategies."}
          </p>

          {/* Collapsible content */}
          <div
            className={`investpara-collapsible ${showIntro ? "open" : "closed"}`}
            aria-hidden={!showIntro}
          >
            <p className="investpara-paragraph">
              {language === "pt"
                ? "Este curso aborda os fundamentos essenciais para quem quer começar a investir em ações: terminologia básica, tipos de ordens, estratégias de trading, gestão de risco e como investir além das fronteiras."
                : "This course covers the essential fundamentals for anyone who wants to start investing in stocks: basic terminology, order types, trading strategies, risk management, and how to invest beyond borders."}
            </p>

            <div className="investpara-block">
              <h3>{language === "pt" ? "Ao final do curso, você será capaz de:" : "By the end of the course, you will be able to:"}</h3>
              <ul>
                <li>{language === "pt" ? "Entender a terminologia básica do mercado de ações" : "Understand basic stock market terminology"}</li>
                <li>{language === "pt" ? "Conhecer os diferentes tipos de ordens de compra e venda" : "Know the different types of buy and sell orders"}</li>
                <li>{language === "pt" ? "Aplicar estratégias básicas de trading" : "Apply basic trading strategies"}</li>
                <li>{language === "pt" ? "Gerenciar riscos de forma eficiente" : "Manage risks efficiently"}</li>
                <li>{language === "pt" ? "Investir em mercados internacionais" : "Invest in international markets"}</li>
              </ul>
            </div>

            <div className="investpara-grid2">
              <div className="investpara-block">
                <h3>{language === "pt" ? "Este curso é para você se:" : "This course is for you if:"}</h3>
                <ul>
                  <li>{language === "pt" ? "É iniciante no mercado de ações" : "You're a beginner in the stock market"}</li>
                  <li>{language === "pt" ? "Quer entender como funciona a bolsa de valores" : "You want to understand how the stock exchange works"}</li>
                  <li>{language === "pt" ? "Deseja aprender a investir de forma consciente" : "You want to learn to invest consciously"}</li>
                </ul>
              </div>

              <div className="investpara-block">
                <h3>{language === "pt" ? "Este curso NÃO é para você se:" : "This course is NOT for you if:"}</h3>
                <ul>
                  <li>{language === "pt" ? "Procura ganhos rápidos sem estudo" : "You're looking for quick gains without study"}</li>
                  <li>{language === "pt" ? "Quer dicas de ações específicas para comprar" : "You want specific stock tips to buy"}</li>
                  <li>{language === "pt" ? "Não tem paciência para aprender os fundamentos" : "You don't have patience to learn the fundamentals"}</li>
                </ul>
              </div>
            </div>

            <div className="investpara-block">
              <h3>{language === "pt" ? "O que está incluso no curso" : "What's included in the course"}</h3>

              <p style={{ margin: "6px 0 12px 0", opacity: 0.9 }}>
                <strong>{language === "pt" ? "6 aulas" : "6 lessons"}</strong> •{" "}
                <strong>{language === "pt" ? "Idioma:" : "Language:"}</strong> {language === "pt" ? "Português e Inglês" : "Portuguese and English"}
              </p>

              <ul>
                <li>{language === "pt" ? "Conteúdo estruturado e didático" : "Structured and educational content"}</li>
                <li>{language === "pt" ? "Materiais de apoio para download" : "Support materials for download"}</li>
                <li>{language === "pt" ? "Acesso vitalício ao conteúdo" : "Lifetime access to content"}</li>
              </ul>
            </div>

            <div className="investpara-block">
              <h3>{language === "pt" ? "Perguntas frequentes" : "Frequently asked questions"}</h3>

              <p>
                <strong>{language === "pt" ? "Preciso ter experiência prévia?" : "Do I need prior experience?"}</strong><br />
                {language === "pt"
                  ? "Não. O curso começa do zero e evolui passo a passo."
                  : "No. The course starts from scratch and progresses step by step."}
              </p>

              <p>
                <strong>{language === "pt" ? "O curso indica ações específicas?" : "Does the course recommend specific stocks?"}</strong><br />
                {language === "pt"
                  ? "Não. O foco é ensinar os fundamentos e estratégias para você tomar suas próprias decisões."
                  : "No. The focus is on teaching fundamentals and strategies so you can make your own decisions."}
              </p>

              <p>
                <strong>{language === "pt" ? "Isso substitui um consultor financeiro?" : "Does this replace a financial advisor?"}</strong><br />
                {language === "pt"
                  ? "Não. O curso é educacional e não oferece recomendação individual."
                  : "No. The course is educational and does not offer individual recommendations."}
              </p>
            </div>
          </div>

          {/* Show more/less button */}
          <button
            type="button"
            className="investpara-toggleLink"
            onClick={() => setShowIntro((v) => !v)}
            aria-expanded={showIntro}
          >
            {showIntro
              ? (language === "pt" ? "Ver menos ↑" : "Show less ↑")
              : (language === "pt" ? "Ver mais sobre o curso ↓" : "Show more about the course ↓")}
          </button>
        </section>

        {/* ===== MODULE ===== */}
        <div className="pf-module module" key={MODULE}>
          <h2
            onClick={() => setShowModule((s) => ({ ...s, [MODULE]: !s[MODULE] }))}
            className="toggle-heading"
            role="button"
            aria-expanded={!!showModule[MODULE]}
            tabIndex={0}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") &&
              setShowModule((s) => ({ ...s, [MODULE]: !s[MODULE] }))
            }
          >
            {translations.module} {MODULE}
            <span className={`arrow ${showModule[MODULE] ? "open" : ""}`}>&gt;</span>
          </h2>

          {showModule[MODULE] && (
            <ul>
              {LESSONS.map(({ id, titleKey }, idx) => (
                <li key={id} className="lesson-row">
                  <button
                    onClick={() => handleLessonClick(id)}
                    className="lesson-link1"
                  >
                    {idx + 1}. {translations[titleKey] || titleKey}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ===== DOWNLOAD ===== */}
        <h3>{translations.downloadfree}</h3>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="lesson-link"
          style={{ padding: "8px 14px", borderRadius: 8 }}
        >
          {downloading
            ? (translations.preparing || "Preparing…")
            : (translations.downloadWord || "Download Word (.docx)")}
        </button>
      </div>
    </div>
  );
};

export default StockTradingBasics;
